import { createClient } from '@/lib/supabase/client'
import type { RecurringTransaction } from '@/lib/types'
import { getCategoryIcon, getCategoryColor } from '@/lib/utils/category-helpers'

/**
 * Calculate the next run date based on frequency and schedule
 */
export function calculateNextRunDate(
  frequency: 'weekly' | 'monthly',
  dayOfWeek: number | null,
  dayOfMonth: number | null
): Date {
  const now = new Date()

  if (frequency === 'weekly' && dayOfWeek !== null) {
    const currentDay = now.getDay()
    const daysUntilNext = (dayOfWeek - currentDay + 7) % 7
    const nextDate = new Date(now)
    nextDate.setDate(now.getDate() + (daysUntilNext === 0 ? 7 : daysUntilNext))
    nextDate.setHours(0, 0, 0, 0)
    return nextDate
  }

  if (frequency === 'monthly' && dayOfMonth !== null) {
    const nextDate = new Date(now.getFullYear(), now.getMonth(), dayOfMonth)
    if (nextDate <= now) {
      nextDate.setMonth(nextDate.getMonth() + 1)
    }
    nextDate.setHours(0, 0, 0, 0)
    return nextDate
  }

  return now
}

/**
 * Fetch all recurring transactions for a user
 */
export async function fetchRecurringTransactions(userId: string): Promise<RecurringTransaction[]> {
  const supabase = createClient()

  const { data, error } = await supabase
    .from('recurring_transactions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching recurring transactions:', error)
    throw new Error('Failed to fetch recurring transactions')
  }

  return data.map((row) => ({
    id: row.id,
    name: row.name,
    amount: row.amount,
    category: row.category,
    type: row.type as 'expense' | 'income',
    frequency: row.frequency as 'weekly' | 'monthly',
    dayOfWeek: row.day_of_week,
    dayOfMonth: row.day_of_month,
    isActive: row.is_active,
    nextRunDate: row.next_run_date,
    icon: getCategoryIcon(row.category),
    color: getCategoryColor(row.category)
  }))
}

/**
 * Create a new recurring transaction
 */
export async function createRecurringTransaction(
  userId: string,
  data: {
    name: string
    amount: number
    category: string
    type: 'expense' | 'income'
    frequency: 'weekly' | 'monthly'
    dayOfWeek?: number | null
    dayOfMonth?: number | null
  }
): Promise<RecurringTransaction> {
  const supabase = createClient()

  const nextRunDate = calculateNextRunDate(
    data.frequency,
    data.dayOfWeek ?? null,
    data.dayOfMonth ?? null
  )

  const { data: result, error } = await supabase
    .from('recurring_transactions')
    .insert({
      user_id: userId,
      name: data.name,
      amount: data.amount,
      category: data.category,
      type: data.type,
      frequency: data.frequency,
      day_of_week: data.dayOfWeek ?? null,
      day_of_month: data.dayOfMonth ?? null,
      is_active: true,
      next_run_date: nextRunDate.toISOString()
    })
    .select()
    .single()

  if (error) {
    console.error('Error creating recurring transaction:', error)
    throw new Error('Failed to create recurring transaction')
  }

  return {
    id: result.id,
    name: result.name,
    amount: result.amount,
    category: result.category,
    type: result.type as 'expense' | 'income',
    frequency: result.frequency as 'weekly' | 'monthly',
    dayOfWeek: result.day_of_week,
    dayOfMonth: result.day_of_month,
    isActive: result.is_active,
    nextRunDate: result.next_run_date,
    icon: getCategoryIcon(result.category),
    color: getCategoryColor(result.category)
  }
}

/**
 * Update a recurring transaction
 */
export async function updateRecurringTransaction(
  id: string,
  data: {
    name?: string
    amount?: number
    category?: string
    type?: 'expense' | 'income'
    frequency?: 'weekly' | 'monthly'
    dayOfWeek?: number | null
    dayOfMonth?: number | null
    isActive?: boolean
  }
): Promise<void> {
  const supabase = createClient()

  const updateData: any = {
    updated_at: new Date().toISOString()
  }

  if (data.name !== undefined) updateData.name = data.name
  if (data.amount !== undefined) updateData.amount = data.amount
  if (data.category !== undefined) updateData.category = data.category
  if (data.type !== undefined) updateData.type = data.type
  if (data.frequency !== undefined) updateData.frequency = data.frequency
  if (data.dayOfWeek !== undefined) updateData.day_of_week = data.dayOfWeek
  if (data.dayOfMonth !== undefined) updateData.day_of_month = data.dayOfMonth
  if (data.isActive !== undefined) updateData.is_active = data.isActive

  // Recalculate next run date if frequency or schedule changed
  if (data.frequency !== undefined || data.dayOfWeek !== undefined ||
      data.dayOfMonth !== undefined) {
    const { data: current } = await supabase
      .from('recurring_transactions')
      .select('*')
      .eq('id', id)
      .single()

    if (current) {
      const nextRunDate = calculateNextRunDate(
        (data.frequency ?? current.frequency) as 'weekly' | 'monthly',
        data.dayOfWeek !== undefined ? data.dayOfWeek : current.day_of_week,
        data.dayOfMonth !== undefined ? data.dayOfMonth : current.day_of_month
      )
      updateData.next_run_date = nextRunDate.toISOString()
    }
  }

  const { error } = await supabase
    .from('recurring_transactions')
    .update(updateData)
    .eq('id', id)

  if (error) {
    console.error('Error updating recurring transaction:', error)
    throw new Error('Failed to update recurring transaction')
  }
}

/**
 * Delete a recurring transaction
 */
export async function deleteRecurringTransaction(id: string): Promise<void> {
  const supabase = createClient()

  const { error } = await supabase
    .from('recurring_transactions')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting recurring transaction:', error)
    throw new Error('Failed to delete recurring transaction')
  }
}

/**
 * Toggle active status of a recurring transaction
 */
export async function toggleRecurringTransaction(id: string, isActive: boolean): Promise<void> {
  await updateRecurringTransaction(id, { isActive })
}

/**
 * Format frequency for display
 */
export function formatFrequency(
  frequency: 'weekly' | 'monthly',
  dayOfWeek: number | null,
  dayOfMonth: number | null
): string {
  if (frequency === 'weekly' && dayOfWeek !== null) {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    return `Every ${days[dayOfWeek]}`
  }

  if (frequency === 'monthly' && dayOfMonth !== null) {
    const suffix = dayOfMonth === 1 ? 'st' : dayOfMonth === 2 ? 'nd' : dayOfMonth === 3 ? 'rd' : 'th'
    return `Monthly on the ${dayOfMonth}${suffix}`
  }

  return 'Not scheduled'
}
